import React, { useMemo } from 'react';
import type { HtmlEmbedComponentProps } from './types';
import { hasValidContent, getContainerStyle } from './embedUtils';
import { Placeholder } from './Placeholder';
import { VideoEmbed } from './VideoEmbed';
import { MapEmbed } from './MapEmbed';
import { SocialEmbed, SocialScripts } from './SocialEmbed';
import { CodeEmbed } from './CodeEmbed';
import { IframeEmbed } from './IframeEmbed';
import { CustomHtmlEmbed } from './CustomHtmlEmbed';

export const HtmlEmbedComponent: React.FC<HtmlEmbedComponentProps> = ({
  component,
  isEditing = false,
}) => {
  const { data } = component;

  const isValid = useMemo(() => hasValidContent(data), [data]);
  const containerStyle = useMemo(
    () => getContainerStyle(data.containerStyle),
    [data.containerStyle]
  );
  const rootStyle: React.CSSProperties = isEditing
    ? { ...containerStyle, position: 'relative', width: '100%', minHeight: '400px' }
    : containerStyle;

  const renderEmbed = () => {
    switch (data.embedType) {
      case 'video':
        if (!data.video) return null;
        return (
          <VideoEmbed config={data.video} responsive={data.responsive} lazyLoad={data.lazyLoad} />
        );

      case 'map':
        if (!data.map) return null;
        return <MapEmbed config={data.map} lazyLoad={data.lazyLoad} />;

      case 'social':
        if (!data.social) return null;
        return <SocialEmbed config={data.social} />;

      case 'code':
        if (!data.code) return null;
        return <CodeEmbed config={data.code} lazyLoad={data.lazyLoad} />;

      case 'iframe':
        if (!data.iframe) return null;
        return <IframeEmbed config={data.iframe} lazyLoad={data.lazyLoad} />;

      case 'custom':
        if (!data.custom) return null;
        return <CustomHtmlEmbed config={data.custom} />;

      default:
        return null;
    }
  };

  return (
    <div
      className="html-embed-component"
      style={rootStyle}
      data-component="html-embed"
      data-embed-type={isValid ? data.embedType : data.embedType || 'none'}
    >
      {isValid ? (
        <>
          {renderEmbed()}
          {data.embedType === 'social' && data.social && (
            <SocialScripts platform={data.social.platform} />
          )}
        </>
      ) : data.fallbackContent ? (
        <div className="embed-fallback text-gray-500 p-4">{data.fallbackContent}</div>
      ) : (
        <Placeholder />
      )}

      {isEditing && <div className="absolute inset-0 z-10" aria-hidden="true" />}
    </div>
  );
};

export default HtmlEmbedComponent;
